import { useState, useCallback, useMemo } from 'react';

/**
 * Multi-row selection for a sheet.
 *
 * Scope is deliberately ONE PAGE AT A TIME. Customers, Brokers and Sellers are
 * server-paginated, so "select all" across pages would need the server to
 * resolve the set — and if a search is active, resolving it correctly means
 * resolving the search too. Rather than build a second, weaker audience
 * mechanism that silently disagrees with the filter on screen, the split is:
 *
 *   checkboxes  → "some of these people"   (this page, hand-picked)
 *   audiences   → "all customers"          (Messages → Compose)
 *
 * Selections survive paging, so ticking three people on page 1 and two on
 * page 2 gives five — the ids are kept, not the rows.
 */
export default function useRowSelection() {
  const [ids, setIds] = useState(() => new Set());

  const toggle = useCallback((id) => {
    setIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  /** Tick every row on the current page, or clear them if all are already ticked. */
  const togglePage = useCallback((pageIds) => {
    setIds((prev) => {
      const next = new Set(prev);
      const allOn = pageIds.length > 0 && pageIds.every((id) => next.has(id));
      for (const id of pageIds) {
        if (allOn) next.delete(id);
        else next.add(id);
      }
      return next;
    });
  }, []);

  const clear = useCallback(() => setIds(new Set()), []);

  return useMemo(() => ({ ids, toggle, togglePage, clear }), [ids, toggle, togglePage, clear]);
}

/**
 * Page-level checkbox state for a header: 'none' | 'some' | 'all'.
 * Split out so both the table header and the mobile toolbar derive it the same
 * way rather than each reimplementing the "indeterminate" rule.
 */
export function pageCheckState(pageIds, ids) {
  if (!pageIds.length) return 'none';
  const on = pageIds.filter((id) => ids.has(id)).length;
  if (on === 0) return 'none';
  return on === pageIds.length ? 'all' : 'some';
}
